import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextCompany } from './Company';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { SettingsDA } from '../../../services/admin/settings.service';
import { ApiDigitalAuthAuthentication } from '../../../services/auth/authentication.service';
import { Error } from '../../../models/error.model';
import { TokenSettings } from '../../../models/token.model';
import { Container } from '../../../styles/container.style';
import { GlobalSwal } from '../../../styles/swal.style';
import { legendIconInvalid, legendValid, legendIconValidRequired, legendIconValidInvalidRestart, legendInvalid, legendValidRequiredRestart, legendValidInvalidRestart } from '../../../utils/legend.data.util';
import { nextForm, previousForm, firstForm, lastForm, removeFirstForm, removeLastForm } from '../../../utils/stepper.util';
import { uploadTooltip } from '../../../utils/tooltip.util';
import { removeUploadFile, uploadImg } from '../../../scripts/file.load.script';
import { evaluateLegendOptionalIcon, evaluateLegendRequiredIcon } from '../../../scripts/validate.legend.script';
import { expressions } from '../../../libraries/regular.expressions.library';
import { taxMethodCompanyList, taxMethodCompanyValue } from '../../../libraries/tax.method.company.library';
import { taxDeclarationCompanyList, taxDeclarationCompanyValue } from '../../../libraries/tax.declaration.company.library';
import { taxPeriodMethodCompanyList, taxPeriodCompanyValue } from '../../../libraries/tax.statement.company.library';
import { allowedPeriodicitiesForMethod, defaultPeriodicityForMethod } from '../../../libraries/tax.statement.company.library';
import { getError } from '../../../languages/translations/response';

import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { authCompanySettings, authSwitchCompany } from '../../../scripts/authentication.script';
import { currencyObject } from '../../../libraries/currency.library';
import { languageList } from '../../../libraries/language.library';

import AppLegend from '../../../components/element/Legend';
import AppInputCountry from '../../../components/element/InputCountry';
import AppInputCurrency from '../../../components/element/InputCurrency';
import AppInputLanguage from '../../../components/element/InputLanguage';
import AppInputQrType from '../../../components/element/InputQrType';
import AppInputAddressAutocomplete from '../../../components/element/InputAddressAutocomplete';
import AppInputCanton from '../../../components/element/InputCanton';
import { countryName } from '../../../libraries/country.library';
import { legalFormList } from '../../../libraries/legal.form.library';
import { ALLOWED_COMPANY_COUNTRIES } from '../../../config/company-constraints';
import { resolveCanton, getCantonName } from '../../../data/cantons';

export interface CompanyCreatePageProps {};

let errorResponse: Error, tokenDataResponse: TokenSettings;

const CompanyCreatePage: React.FunctionComponent<CompanyCreatePageProps> = props => {
  const {setRoute, appServiceForUser, reloadSession} = UseOutletContextCompany()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [logo, setLogo] = useState({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: true, message: ''}})
  const [name, setName] = useState({value: '', valid: false})
  const [street, setStreet] = useState({value: '', place: '', valid: false})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: false})
  const [additional, setAdditional] = useState({value: '', valid: true})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: false})
  const [city, setCity] = useState({value: '', valid: false})
  const [zipCode, setZipCode] = useState({value: '', valid: false})
  const [country, setCountry] = useState({value: 'CH', valid: true})
  const [email, setEmail] = useState({value: '', valid: false})
  const [phone, setPhone] = useState({value: '', valid: false})
  const [website, setWebsite] = useState({value: '', valid: true})
  const [vatNumber, setVatNumber] = useState({value: '', valid: true})
  const [registerNumber, setRegisterNumber] = useState({value: '', valid: true})
  const [currency, setCurrency] = useState({value: '', valid: false, object: null as any})
  const [subjectTax, setSubjectTax] = useState({value: '', valid: false})
  const [taxDeclaration, setTaxDeclaration] = useState({value: '', valid: true})
  const [taxMethod, setTaxMethod] = useState({value: '', valid: true})
  const [taxRate, setTaxRate] = useState({value: '', valid: true})
  const [declarationPeriodicity, setDeclarationPeriodicity] = useState({value: '', valid: true})
  const [fiscalYearStart, setFiscalYearStart] = useState({value: '', valid: false})
  const [fiscalYearClosingYear, setFiscalYearClosingYear] = useState({value: '', valid: false})
  const [language, setLanguage] = useState({value: '', valid: false})
  const [qrType, setQrType] = useState({value: '', valid: false})
  const [legalForm, setLegalForm] = useState({value: '', valid: true})

  /**
   * Wrapper for InputAddressAutocomplete: resolves Google's administrative_area_level_1
   * long_name to a canton code. If no match, leaves the selector empty.
   */
  const setAdministrativeAreaFromGoogle = useCallback(
    (val: React.SetStateAction<{value: string, valid: boolean}>) => {
      const raw = typeof val === 'function' ? val({value: '', valid: false}) : val
      const resolved = resolveCanton(raw.value)
      setAdministrativeArea(resolved ? { value: resolved.code, valid: true } : { value: '', valid: false })
    },
    []
  )

  const submitCompanyCreate = async () => {
    setLoadIndicator('on')

    if (appServiceForUser && language.valid && qrType.valid) {
      let addressStreetNumber = (streetNumber.value === 'NN' || streetNumber.value === 'nn') ? streetNumber.value.toUpperCase() : streetNumber.value
      // Send FR canonical name to preserve current backend contract (label, not code).
      // When the migration to codes happens, remove this conversion.
      let cantonLabel = getCantonName(administrativeArea.value, 'fr')
      let isSubjectTax = subjectTax.value === "yes"

      const fiscalYearEnd = fiscalYearClosingYear.valid ? `${fiscalYearClosingYear.value}-12-31` : ''

      await SettingsDA.postSettings(appServiceForUser.api_key, name.value, logo.value.file, street.value, addressStreetNumber, additional.value, cantonLabel, city.value, zipCode.value, country.value, email.value, phone.value, website.value, registerNumber.value, vatNumber.value, currency.value, isSubjectTax, taxDeclaration.value, taxMethod.value, taxRate.value, language.value, qrType.value, isSubjectTax ? declarationPeriodicity.value : '', fiscalYearStart.value, fiscalYearEnd, legalForm.value).then( async (response) => {
        if (response.status === 200 || response.status === 201) {
          tokenDataResponse = response.data
          authCompanySettings(tokenDataResponse)

          const companyId = tokenDataResponse?.company?.id ?? null

          if (companyId) {
            // Token from POST /companies has companyId=null in JWT
            // Call switch-company to get a company-scoped token
            try {
              const switchResponse = await ApiDigitalAuthAuthentication.switchCompany(
                appServiceForUser.api_key,
                tokenDataResponse.refresh_token,
                companyId
              )

              if (switchResponse.status === 200 && switchResponse.data) {
                const switchData = switchResponse.data
                authSwitchCompany(switchData)
              } else {
                console.warn('Switch-company failed after creation, status:', switchResponse.status)
              }
            } catch (switchError) {
              console.warn('Switch-company error after creation:', switchError)
            }
          }

          reloadSession(null, appServiceForUser.api_key, companyId, async () => {
            GlobalSwal.fire({
              title: lang.labels.successfullyCreatedCompany,
              text: lang.labels.newCompanyIsSelectedByDefault,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              if (companyId) {
                navigate('/main/dashboard', {replace: true})
              } else {
                window.location.reload()
              }
            })
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!language.value) legendInvalid('legend-language-required')
        if (!qrType.value) legendInvalid('legend-qr-type-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeLogo = async (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setLogo(await uploadImg(lang, event.target.files[0]))
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeStreetNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({...streetNumber, value: event.target.value})
  }

  const handleChangeAdditional = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdditional({...additional, value: event.target.value})
  }

  const handleChangeCity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCity({...city, value: event.target.value})
  }

  const handleChangeZipCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setZipCode({...zipCode, value: event.target.value})
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const handleChangePhone = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPhone({...phone, value: event.target.value})
  }

  const handleChangeWebsite = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setWebsite({...website, value: event.target.value})
  }

  const handleChangeRegisterNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setRegisterNumber({...registerNumber, value: event.target.value})
  }

  const handleChangeVatNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVatNumber({...vatNumber, value: event.target.value})
  }

  const handleChangeTaxRate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setTaxRate({...taxRate, value: event.target.value})
  }

  const handleChangeSubjectTax = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setSubjectTax({value: event.target.value, valid: true})
    setTaxDeclaration({value: '', valid: event.target.value === 'no'})
    setTaxMethod({value: '', valid: event.target.value === 'no'})
    setTaxRate({value: '', valid: true})
    setDeclarationPeriodicity({value: '', valid: event.target.value === 'no'})

    legendValidInvalidRestart('legend-tax-declaration-required')
    legendValidInvalidRestart('legend-tax-method-required')
    legendValidInvalidRestart('legend-declaration-periodicity-required')
    legendIconValidInvalidRestart('input-tax-rate', 'legend-tax-rate-valid')
    legendIconValidInvalidRestart('input-tax-rate', 'legend-tax-rate-required')
  }

  const handleChangeTaxDeclaration = (item: string) => {
    setTaxDeclaration({value: item, valid: true})
    legendValid('legend-tax-declaration-required')
  }

  const handleChangeTaxMethod = (item: string) => {
    setTaxMethod({value: item, valid: true})
    setTaxRate({value: '', valid: !(item === 'tdfn' || item === 'taf')})

    const allowed = allowedPeriodicitiesForMethod(item)
    if (declarationPeriodicity.value && !allowed.includes(declarationPeriodicity.value)) {
      const def = defaultPeriodicityForMethod(item)
      setDeclarationPeriodicity({value: def, valid: !!def})
    }

    legendValid('legend-tax-method-required')
    legendValidInvalidRestart('legend-declaration-periodicity-required')
    legendIconValidInvalidRestart('input-tax-rate', 'legend-tax-rate-valid')
    legendIconValidInvalidRestart('input-tax-rate', 'legend-tax-rate-required')
  }

  const handleChangeDeclarationPeriodicity = (item: string) => {
    setDeclarationPeriodicity({value: item, valid: true})
    legendValid('legend-declaration-periodicity-required')
  }

  const handleChangeFiscalYearStart = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setFiscalYearStart({value, valid: !!value})
    if (value) {
      const year = new Date(value).getFullYear()
      setFiscalYearClosingYear({value: year.toString(), valid: true})
    } else {
      setFiscalYearClosingYear({value: '', valid: false})
    }
  }

  const handleChangeFiscalYearClosingYear = (year: string) => {
    setFiscalYearClosingYear({value: year, valid: true})
  }

  const handleRemoveLogo = () => {
    setLogo(removeUploadFile())
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, 'input-name', 'legend-name-valid', 'legend-name-required')
  }

  const validateStreetNumber = () => {
    if (streetNumber.value === 'NN' || streetNumber.value === 'nn') {
      setStreetNumber({...streetNumber, valid: true})
      legendIconValidRequired('input-street-number', 'legend-street-number-valid', 'legend-street-number-required')
    } else {
      evaluateLegendRequiredIcon(expressions.streetnumber, streetNumber, setStreetNumber, 'input-street-number', 'legend-street-number-valid', 'legend-street-number-required')
    }
  }

  const validateAdditional = () => {
    evaluateLegendOptionalIcon(expressions.text, additional, setAdditional, 'input-additional', 'legend-additional-valid')
  }

  const validateCity = () => {
    evaluateLegendRequiredIcon(expressions.location, city, setCity, 'input-city', 'legend-city-valid', 'legend-city-required')
  }

  const validateZipCode = () => {
    evaluateLegendRequiredIcon(expressions.zipcode, zipCode, setZipCode, 'input-zip-code', 'legend-zip-code-valid', 'legend-zip-code-required')
  }

  const validateEmail = () => {
    evaluateLegendRequiredIcon(expressions.email, email, setEmail, 'input-email', 'legend-email-valid', 'legend-email-required')
  }

  const validatePhone = () => {
    evaluateLegendRequiredIcon(expressions.phone, phone, setPhone, 'input-phone', 'legend-phone-valid', 'legend-phone-required')
  }

  const validateWebsite = () => {
    evaluateLegendOptionalIcon(expressions.website, website, setWebsite, 'input-website', 'legend-website-valid')
  }

  const validateVatNumber = () => {
    evaluateLegendOptionalIcon(expressions.vat, vatNumber, setVatNumber, 'input-vat-number', 'legend-vat-number-valid')
  }

  const validateRegisterNumber = () => {
    evaluateLegendOptionalIcon(expressions.registernumber, registerNumber, setRegisterNumber, 'input-register-number', 'legend-register-number-valid')
  }

  const validateTaxRate = () => {
    evaluateLegendRequiredIcon(expressions.tax, taxRate, setTaxRate, 'input-tax-rate', 'legend-tax-rate-valid', 'legend-tax-rate-required')
  }

  const executeFormPrevious = () => {
    switch (true) {
      case document.getElementById('form-title-4')?.classList.contains('current'):
        removeLastForm('container-stepper-form')
        previousForm('form-title-3', 'form-title-4', 'form-container-3', 'form-container-4')
        break;
      case document.getElementById('form-title-3')?.classList.contains('current'):
        previousForm('form-title-2', 'form-title-3', 'form-container-2', 'form-container-3')
        break;
      case document.getElementById('form-title-2')?.classList.contains('current'):
        firstForm('container-stepper-form')
        previousForm('form-title-1', 'form-title-2', 'form-container-1', 'form-container-2')
        break;
    }

    uploadTooltip()
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const executeFormNext = () => {
    switch (true) {
      case document.getElementById('form-title-1')?.classList.contains('current'):
        if (name.valid === true) {
          removeFirstForm('container-stepper-form')
          nextForm('form-title-1', 'form-title-2', 'form-container-1', 'form-container-2')
          if (!logo.valid) {
            setLogo(removeUploadFile())
            legendValidRequiredRestart('legend-logo-type', 'legend-logo-size')
          }
        } else {
          GlobalSwal.fire({
            text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            if (!name.value) legendIconInvalid('input-name', 'legend-name-required')
          })
        }
        break;
      case document.getElementById('form-title-2')?.classList.contains('current'):
        if (street.valid && streetNumber.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid && email.valid && phone.valid) {
          nextForm('form-title-2', 'form-title-3', 'form-container-2', 'form-container-3')
          if (!additional.valid) {
            setAdditional({value: '', valid: true})
            legendIconValidInvalidRestart('input-additional', 'legend-additional-valid')
          }
          if (!website.valid) {
            setWebsite({value: '', valid: true})
            legendIconValidInvalidRestart('input-website', 'legend-website-valid')
          }
        } else {
          GlobalSwal.fire({
            text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            if (!street.value) legendIconInvalid('input-street', 'legend-street-required')
            if (!streetNumber.value) legendIconInvalid('input-street-number', 'legend-street-number-required')
            if (!administrativeArea.value) legendInvalid('legend-administrative-area-required')
            if (!city.value) legendIconInvalid('input-city', 'legend-city-required')
            if (!zipCode.value) legendIconInvalid('input-zip-code', 'legend-zip-code-required')
            if (!country.value) legendInvalid('legend-country-required')
            if (!email.value) legendIconInvalid('input-email', 'legend-email-required')
            if (!phone.value) legendIconInvalid('input-phone', 'legend-phone-required')
          })
        }
        break;
      case document.getElementById('form-title-3')?.classList.contains('current'):
        if (currency.valid && subjectTax.valid && taxDeclaration.valid && taxMethod.valid && taxRate.valid && declarationPeriodicity.valid && fiscalYearStart.valid && fiscalYearClosingYear.valid) {
          lastForm('container-stepper-form')
          nextForm('form-title-3', 'form-title-4', 'form-container-3', 'form-container-4')
          if (!registerNumber.valid) {
            setRegisterNumber({value: '', valid: true})
            legendIconValidInvalidRestart('input-register-number', 'legend-register-number-valid')
          }
          if (!vatNumber.valid) {
            setVatNumber({value: '', valid: true})
            legendIconValidInvalidRestart('input-vat-number', 'legend-vat-number-valid')
          }
        } else {
          GlobalSwal.fire({
            text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            if (!currency.value) legendInvalid('legend-currency-required')
            if (!subjectTax.value) legendInvalid('legend-subject-tax-required')
            if (subjectTax.value === 'yes') {
              if (!taxDeclaration.value) legendInvalid('legend-tax-declaration-required')
              if (!taxMethod.value) legendInvalid('legend-tax-method-required')
              if ((taxMethod.value === 'tdfn' || taxMethod.value === 'taf') && !taxRate.value) legendIconInvalid('input-tax-rate', 'legend-tax-rate-required')
              if (!declarationPeriodicity.value) legendInvalid('legend-declaration-periodicity-required')
            }
            if (!fiscalYearStart.value) legendInvalid('legend-fiscal-year-start-required')
            if (!fiscalYearClosingYear.value) legendInvalid('legend-fiscal-year-closing-required')
          })
        }
        break;
    }

    uploadTooltip()
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.companies, branch: lang.labels.createCompany}, company: false})
    setLanguage({value: languageList(lang).some((item) => (item.code === lang.code)) ? lang.code : '', valid: languageList(lang).some((item) => (item.code === lang.code))})
    setCurrency({value: 'CHF', valid: true, object: currencyObject(language.valid ? language.value.toUpperCase() : lang.code, 'CHF')})
    setQrType({value: 'no-reference', valid: true})
    uploadTooltip()

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="text-dark text-center text-uppercase mt-3 mb-0">{lang.labels.newCompany}</h3>
        <div id="container-stepper-form" className="stepper stepper-links d-flex flex-column">
          <div className="stepper-nav d-flex justify-content-center align-content-center py-5">
            <div className="stepper-item">
              <span className="svg-icon svg-icon-primary svg-icon-2x">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 4L18 12L10 20H14L21.3 12.7C21.7 12.3 21.7 11.7 21.3 11.3L14 4H10Z" fill="black"/>
                  <path opacity="0.3" d="M3 4L11 12L3 20H7L14.3 12.7C14.7 12.3 14.7 11.7 14.3 11.3L7 4H3Z" fill="black"/>
                </svg>
              </span>
            </div>
            <div id="form-title-1" className="stepper-item current" data-kt-stepper-element="nav">
              <h3 className="stepper-title">{lang.labels.identity}</h3>
            </div>
            <div id="form-title-2" className="stepper-item pending" data-kt-stepper-element="nav">
              <h3 className="stepper-title">{lang.labels.contact}</h3>
            </div>
            <div id="form-title-3" className="stepper-item pending" data-kt-stepper-element="nav">
              <h3 className="stepper-title">{lang.labels.legal}</h3>
            </div>
            <div id="form-title-4" className="stepper-item pending" data-kt-stepper-element="nav">
              <h3 className="stepper-title">{lang.labels.settings}</h3>
            </div>
            <div className="stepper-item">
              <span className="svg-icon svg-icon-primary svg-icon-2x">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.6 4L6.6 12L14.6 20H10.6L3.3 12.7C2.9 12.3 2.9 11.7 3.3 11.3L10.6 4H14.6Z" fill="black"/>
                  <path opacity="0.3" d="M21.6 4L13.6 12L21.6 20H17.6L10.3 12.7C9.9 12.3 9.9 11.7 10.3 11.3L17.6 4H21.6Z" fill="black"/>
                </svg>
              </span>
            </div>
          </div>
          <div className="form mx-auto mw-700px w-100 py-5">
            <div id="form-container-1" className="current" data-kt-stepper-element="content">
              <div className="w-100">
                <div className="pb-10">
                  <h2 className="text-dark">{lang.labels.companyIdentity}</h2>
                  <div className="text-muted fw-bold">{lang.labels.aNewCompanyWillBeCreated}</div>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.companyName}</label>
                  <input id="input-name" className="form-control" type="text" autoComplete="off" name="name" value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                  <AppLegend attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label">{lang.labels.legalForm}</label>
                  <Form.Select className="form-select" name="legal_form" value={legalForm.value} onChange={(e) => setLegalForm({value: e.target.value, valid: true})}>
                    <option value="">{lang.labels.select}</option>
                    { legalFormList(lang).map((item, index) => (
                      <option key={index} value={item.code}>{item.display}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="fv-row">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      {lang.labels.companyLogo}
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.dragOrClickOnTheBoxToAddTheCompanyLogo}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <div className="row mb-2" data-kt-buttons="true">
                    <div className="col">
                      <label className="btn btn-outline btn-outline-dashed btn-outline-default w-100 p-4">
                        <input className="btn-check" type="file" name="logo" accept=".png, .jpg, .jpeg" onChange={handleChangeLogo} />
                        { logo.value.file
                          ?
                          <img src={logo.value.view} alt={logo.value.name} className="rounded w-100" />
                          :
                          <span className="fw-bolder fs-3">{lang.labels.addImage}</span>
                        }
                      </label>
                    </div>
                  </div>
                  <Container property={(logo.value.view.length === 0).toString()}>
                    <div className="form-text text-justify">{lang.labels.theFileMustBeFormat}</div>
                  </Container>
                  <Container property={(logo.value.view.length > 0 && logo.value.file !== null).toString()}>
                    <div className="d-flex justify-content-end">
                      <button className="btn btn-sm btn-danger text-uppercase" type="button" data-kt-image-input-action="remove" onClick={handleRemoveLogo}>
                        {lang.labels.removeLogo}
                      </button>
                    </div>
                  </Container>
                  <div className={`fv-plugins-message-container invalid-feedback ${logo.valid.file && "d-none"}`}>
                    <div data-field="document" data-validator="file">{logo.valid.message}</div>
                  </div>
                </div>
              </div>
            </div>
            <div id="form-container-2" className="pending" data-kt-stepper-element="content">
              <div className="w-100">
                <div className="pb-10">
                  <h2 className="text-dark">{lang.labels.contactInformation}</h2>
                  <div className="text-muted fw-bold">{lang.labels.aNewCompanyWillBeCreated}</div>
                </div>
                <div className="row fv-row mb-5">
                  <div className="col-xl-8 mb-5 mb-xl-0">
                    <label className="form-label required">{lang.labels.street}</label>
                    <AppInputAddressAutocomplete street={street} setStreet={setStreet} setStreetNumber={setStreetNumber} setAdditional={setAdditional} setAdministrativeArea={setAdministrativeAreaFromGoogle} setCity={setCity} setZipCode={setZipCode} setCountry={setCountry}></AppInputAddressAutocomplete>
                  </div>
                  <div className="col-xl-4">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        <span className="required">{lang.labels.number}</span>
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifAddressDoesNotHaveANumber}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <input id="input-street-number" className="form-control" type="text" autoComplete="off" name="street-number" placeholder={lang.labels.noNumber} value={streetNumber.value} onChange={handleChangeStreetNumber} onKeyUp={validateStreetNumber} onBlur={validateStreetNumber} />
                    <AppLegend attribute={{check: streetNumber.valid, name: "street-number", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      {lang.labels.additional}
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifNecessaryEnterSomethingReferentialRegardingAddress}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <input id="input-additional" className="form-control" type="text" autoComplete="off" name="additional" value={additional.value} onChange={handleChangeAdditional} onKeyUp={validateAdditional} onBlur={validateAdditional} />
                  <AppLegend attribute={{check: additional.valid, name: "additional", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.administrativeArea}</label>
                  <AppInputCanton options={{ disabled: false }} canton={administrativeArea} setCanton={setAdministrativeArea} />
                </div>
                <div className="row fv-row mb-5">
                  <div className="col-xl-4 mb-5 mb-xl-0">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        <span className="required">{lang.labels.zipCode}</span>
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterThePostalCodeOfTheCity}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <input id="input-zip-code" className="form-control" type="text" autoComplete="off" name="zip-code" value={zipCode.value} onChange={handleChangeZipCode} onKeyUp={validateZipCode} onBlur={validateZipCode} />
                    <AppLegend attribute={{check: zipCode.valid, name: "zip-code", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="col-xl-8">
                    <label className="form-label required">{lang.labels.city}</label>
                    <input id="input-city" className="form-control" type="text" autoComplete="off" name="city" value={city.value} onChange={handleChangeCity} onKeyUp={validateCity} onBlur={validateCity} />
                    <AppLegend attribute={{check: city.valid, name: "city", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.country}</label>
                  {ALLOWED_COMPANY_COUNTRIES.length === 1
                    ? <input className="form-control" type="text" value={countryName(lang.code, ALLOWED_COMPANY_COUNTRIES[0]) || ALLOWED_COMPANY_COUNTRIES[0]} disabled />
                    : <AppInputCountry options={{small: false, disabled: false, placeholder: null}} country={country} setCountry={setCountry}></AppInputCountry>
                  }
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.email}</label>
                  <input id="input-email" className="form-control" type="email" autoComplete="off" name="email" value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
                  <AppLegend attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.phoneNumber}</label>
                  <input id="input-phone" className="form-control" type="tel" autoComplete="off" name="phone" value={phone.value} onChange={handleChangePhone} onKeyUp={validatePhone} onBlur={validatePhone} />
                  <AppLegend attribute={{check: phone.valid, name: "phone", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row">
                  <label className="form-label">{lang.labels.webSite}</label>
                  <input id="input-website" className="form-control" type="url" autoComplete="off" name="website" value={website.value} onChange={handleChangeWebsite} onKeyUp={validateWebsite} onBlur={validateWebsite} />
                  <AppLegend attribute={{check: website.valid, name: "website", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
            </div>
            <div id="form-container-3" className="pending" data-kt-stepper-element="content">
              <div className="w-100">
                <div className="pb-10">
                  <h2 className="text-dark">{lang.labels.legalInformation}</h2>
                  <div className="text-muted fw-bold">{lang.labels.aNewCompanyWillBeCreated}</div>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      {lang.labels.vatNumber}
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.identificationNumberForACompany}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <input id="input-vat-number" className="form-control" type="text" autoComplete="off" name="vat-number" value={vatNumber.value} onChange={handleChangeVatNumber} onKeyUp={validateVatNumber} onBlur={validateVatNumber} />
                  <AppLegend attribute={{check: vatNumber.valid, name: "vat-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row mb-5 d-none">
                  <label className="form-label">{lang.labels.registerNumber}</label>
                  <input id="input-register-number" className="form-control" type="text" autoComplete="off" name="register-number" value={registerNumber.value} onChange={handleChangeRegisterNumber} onKeyUp={validateRegisterNumber} onBlur={validateRegisterNumber} />
                  <AppLegend attribute={{check: registerNumber.valid, name: "register-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row mb-10">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      {lang.labels.currency}
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.basicCurrencyCompany}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
                </div>
                <div className="d-flex flex-stack">
                  <div className="me-5">
                    <label className="form-label m-0">{lang.labels.isCompanySubjectVat}</label>
                    <div className="text-muted fs-8">{lang.labels.ifSoAdditionalInformationRequired}</div>
                  </div>
                  <div className="d-flex row mx-0">
                    <label className="col-6 form-check form-check-lg form-check-custom form-check-solid">
                      <input className="form-check-input" type="radio" value="yes" checked={subjectTax.value === "yes"} onChange={handleChangeSubjectTax} />
                      <span className="form-check-label fw-bold text-uppercase fs-7">{lang.labels.yes}</span>
                    </label>
                    <label className="col-6 form-check form-check-lg form-check-custom form-check-solid">
                      <input className="form-check-input" type="radio" value="no" checked={subjectTax.value === "no"} onChange={handleChangeSubjectTax} />
                      <span className="form-check-label fw-bold text-uppercase fs-7">{lang.labels.no}</span>
                    </label>
                  </div>
                </div>
                <AppLegend attribute={{check: subjectTax.valid, name: "subject-tax", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                <Container property={(subjectTax.valid && subjectTax.value === "yes").toString()}>
                  <div className="fv-row mt-5">
                    <label className="form-label">
                      <span className="required">{lang.labels.vatAccountingType}</span>
                      <div className="text-muted fs-8">{lang.labels.selectApplicableScheme}</div>
                    </label>
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                              {taxMethod.valid ? taxMethodCompanyValue(lang, taxMethod.value) : lang.labels.selectOption}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              { taxMethodCompanyList(lang).map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === taxMethod.value && "select2-results__option--selected"}`} role="option" aria-selected={item.code === taxMethod.value}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxMethod(item.code)}>{item.display}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                    <AppLegend attribute={{check: taxMethod.valid, name: "tax-method", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                  <Container property={(taxMethod.value === 'tdfn' || taxMethod.value === 'taf').toString()}>
                    <div className="fv-row mt-5">
                      <label className="form-label">
                        <span className="required">{lang.labels.taxRate}</span>
                        <div className="text-muted fs-8">{lang.labels.enterApplicableVatRate}</div>
                      </label>
                      <input id="input-tax-rate" className="form-control" type="text" autoComplete="off" name="tax-rate" value={taxRate.value} onChange={handleChangeTaxRate} onKeyUp={validateTaxRate} onBlur={validateTaxRate} />
                      <AppLegend attribute={{check: taxRate.valid, name: "tax-rate", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                  </Container>
                  <div className="fv-row mt-5">
                    <label className="form-label">
                      <span className="required">{lang.labels.declarationMethod}</span>
                      <div className="text-muted fs-8">{lang.labels.howWouldLikeDeclareVat}</div>
                    </label>
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                              {taxDeclaration.valid ? taxDeclarationCompanyValue(lang, taxDeclaration.value) : lang.labels.selectOption}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              { taxDeclarationCompanyList(lang).map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === taxDeclaration.value && "select2-results__option--selected"}`} role="option" aria-selected={item.code === taxDeclaration.value}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxDeclaration(item.code)}>{item.display}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                    <AppLegend attribute={{check: taxDeclaration.valid, name: "tax-declaration", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mt-5">
                    <label className="form-label">
                      <span className="required">{lang.labels.vatPeriodicity}</span>
                    </label>
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                              {declarationPeriodicity.valid && declarationPeriodicity.value ? taxPeriodCompanyValue(lang, declarationPeriodicity.value) : lang.labels.selectOption}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              { taxPeriodMethodCompanyList(lang, taxMethod.value).map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === declarationPeriodicity.value && "select2-results__option--selected"}`} role="option" aria-selected={item.code === declarationPeriodicity.value}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeDeclarationPeriodicity(item.code)}>{item.display}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                    <AppLegend attribute={{check: declarationPeriodicity.valid, name: "declaration-periodicity", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </Container>
                <div className="fv-row mt-10">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      <span className="required">{lang.labels.fiscalYearStartDate}</span>
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.fiscalYearEndForced}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <input id="input-fiscal-year-start" className="form-control" type="date" value={fiscalYearStart.value} onChange={handleChangeFiscalYearStart} />
                  <AppLegend attribute={{check: fiscalYearStart.valid, name: "fiscal-year-start", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <Container property={(fiscalYearStart.valid).toString()}>
                  <div className="row fv-row mt-5">
                    <div className="col-xl-6 mb-5 mb-xl-0">
                      <label className="form-label">
                        <span className="required">{lang.labels.fiscalYearClosingYear}</span>
                      </label>
                      <Dropdown>
                        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                          <span className="selection">
                            <span className="select2-selection select2-selection--single form-select">
                              <span className="select2-selection__rendered" role="textbox">
                                {fiscalYearClosingYear.valid ? fiscalYearClosingYear.value : lang.labels.selectOption}
                              </span>
                            </span>
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                          <span className="select2-dropdown select2-dropdown--below">
                            <span className="select2-results">
                              <ul className="select2-results__options" role="listbox">
                                { [new Date(fiscalYearStart.value).getFullYear(), new Date(fiscalYearStart.value).getFullYear() + 1].map((year, index) => (
                                  <li key={index} className={`select2-results__option select2-results__option--selectable ${year.toString() === fiscalYearClosingYear.value && "select2-results__option--selected"}`} role="option" aria-selected={year.toString() === fiscalYearClosingYear.value}>
                                    <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeFiscalYearClosingYear(year.toString())}>{year}</Dropdown.Item>
                                  </li>
                                ))}
                              </ul>
                            </span>
                          </span>
                        </Dropdown.Menu>
                      </Dropdown>
                      <AppLegend attribute={{check: fiscalYearClosingYear.valid, name: "fiscal-year-closing", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className="col-xl-6">
                      <label className="form-label">{lang.labels.fiscalYearEndDate}</label>
                      <input className="form-control" type="text" value={fiscalYearClosingYear.valid ? `31.12.${fiscalYearClosingYear.value}` : ''} disabled />
                      <div className="form-text">{lang.labels.fiscalYearEndForced}</div>
                    </div>
                  </div>
                </Container>
              </div>
            </div>
            <div id="form-container-4" className="pending" data-kt-stepper-element="content">
              <div className="w-100">
                <div className="pb-10">
                  <h2 className="text-dark">{lang.labels.settingInformation}</h2>
                  <div className="text-muted fw-bold">{lang.labels.aNewCompanyWillBeCreated}</div>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      <span className="required">{lang.labels.language}</span>
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.languageToDownload}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <AppInputLanguage language={language} setLanguage={setLanguage}></AppInputLanguage>
                </div>
                <div className="fv-row">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      <span className="required">{lang.labels.qrType}</span>
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.shapesOrVersionsOfQR}><FaExclamationCircle /></i>
                    </div>
                  </label>
                  <AppInputQrType qrType={qrType} setQrType={setQrType}></AppInputQrType>
                </div>
              </div>
            </div>
            <div className="d-flex flex-stack pt-15">
              <div>
                <button className="btn btn-light-primary me-3" type="button" data-kt-stepper-action="previous" onClick={executeFormPrevious}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-3 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="6" y="11" width="13" height="2" rx="1" fill="black" />
                        <path d="M8.56569 11.4343L12.75 7.25C13.1642 6.83579 13.1642 6.16421 12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75L5.70711 11.2929C5.31658 11.6834 5.31658 12.3166 5.70711 12.7071L11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25C13.1642 17.8358 13.1642 17.1642 12.75 16.75L8.56569 12.5657C8.25327 12.2533 8.25327 11.7467 8.56569 11.4343Z" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.back}
                  </div>
                </button>
              </div>
              <div className="ms-3">
                <button className="btn btn-primary" type="button" data-kt-stepper-action="next" onClick={executeFormNext}>
                  <div className="d-flex align-items-center">
                    {lang.labels.continue}
                    <span className="svg-icon svg-icon-3 me-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="18" y="13" width="13" height="2" rx="1" transform="rotate(-180 18 13)" fill="black" />
                        <path d="M15.4343 12.5657L11.25 16.75C10.8358 17.1642 10.8358 17.8358 11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25L18.2929 12.7071C18.6834 12.3166 18.6834 11.6834 18.2929 11.2929L12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75C10.8358 6.16421 10.8358 6.83579 11.25 7.25L15.4343 11.4343C15.7467 11.7467 15.7467 12.2533 15.4343 12.5657Z" fill="black" />
                      </svg>
                    </span>
                  </div>
                </button>
                <button className="btn btn-primary" data-kt-stepper-action="submit" type="button" data-kt-indicator={loadIndicator} onClick={submitCompanyCreate}>
                  <span className="indicator-label">
                    <div className="d-flex align-items-center">
                      {lang.labels.create}
                      <span className="svg-icon svg-icon-3 me-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 4L18 12L10 20H14L21.3 12.7C21.7 12.3 21.7 11.7 21.3 11.3L14 4H10Z" fill="black"/>
                          <path opacity="0.3" d="M3 4L11 12L3 20H7L14.3 12.7C14.7 12.3 14.7 11.7 14.3 11.3L7 4H3Z" fill="black"/>
                        </svg>
                      </span>
                    </div>
                  </span>
                  <span className="indicator-progress">
                    {lang.labels.pleaseWait}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
};

export default CompanyCreatePage;
